import dayjs from 'dayjs';
import _ from 'lodash';

import feedIcon from 'img/ucd_channelgroup_feed.svg';
import hedwigIcon from 'img/ucd_channelgroup_hedwig.svg';
import pmIcon from 'img/ucd_channelgroup_pm.svg';
import salesforceIcon from 'img/ucd_channelgroup_salesforce.svg';
import tridentIcon from 'img/ucd_channelgroup_trident.svg';
import multistepJourneyIcon from 'img/ucd_multistep_journey.svg';
import userAcquisitionIcon from 'img/ucd_objective_acquistion.svg';
import brandAwarenessIcon from 'img/ucd_objective_brand_awareness.svg';
import communicationsIcon from 'img/ucd_objective_communication.svg';
import crossSellIcon from 'img/ucd_objective_crosssell.svg';
import demandGenerationIcon from 'img/ucd_objective_demand_generation.svg';
import userReactivationIcon from 'img/ucd_objective_user_reactivation.svg';
import userRetentionIcon from 'img/ucd_objective_user_retention.svg';
import simpleScenarioIcon from 'img/ucd_scenario_icon.svg';
import simpleScheduleIcon from 'img/ucd_schedule_icon.svg';
import React from 'react';

import {
  colorBaseBlue300,
  colorBrandPrimaryDefault,
  colorBrandPrimarySoft,
  colorBrandSecondaryBoldest,
  colorBrandSecondaryDefault,
  colorBrandSecondarySoftest,
  colorStatusInformativeBold,
  colorStatusInformativeSoftest,
  colorStatusNoticeAltBold,
  colorStatusNoticeAltSoftest,
  colorStatusNoticeBoldest,
  colorStatusNoticeSoftest,
  colorStatusPositiveBold,
} from '@duxton-web/design-tokens/themes/light';
import {
  FeedbackOutlined,
  KnowledgeUniversal,
  NotificationsOutlined,
  OffersOutlined,
} from '@duxton-web/icons';

export const PROJECT_MORE_FILTERS_KEYS = {
  AUDIENCE: 'audience',
  OBJECTIVE: 'objective',
  CREATOR: 'creator',
};

export const CAMPAIGN_MORE_FILTERS_KEYS = {
  AUDIENCE: 'audience',
  CREATOR: 'creator',
  CHANNEL_GROUP: 'channelGroup',
  CHANNEL: 'channel',
  START: 'start',
  END: 'end',
  TEMPLATE_TYPE: 'templateType',
};

export const PROJECT_COUNTRY_QUERY_KEY = 'project_country';
export const PROJECT_OWNERSHIP_QUERY_KEY = 'project_ownership';
export const PROJECT_STATUS_QUERY_KEY = 'project_status';
export const PROJECT_PAGE_QUERY_KEY = 'project_page';
export const PROJECT_PAGE_SIZE_QUERY_KEY = 'project_page_size';
export const PROJECT_MORE_FILTERS_QUERY_KEY = 'project_more_filters';
export const PROJECT_SEARCH_QUERY_KEY = 'project_search';
export const CURRENT_TAB_QUERY_KEY = 'tab';

export const AUDIENCE_TYPES = {
  pax: 'pax',
  dax: 'dax',
  mex: 'mex',
};

// INTERNAL_STATUS are the status we store internally
export const INTERNAL_STATUS = {
  draft: 'DRAFT',
  active: 'ACTIVE',
  deactivated: 'DEACTIVATED',
};

// STATUS are the status used for display
export const STATUS = {
  draft: 'DRAFT',
  pendingCreation: 'PENDING-CREATION',
  pendingApproval: 'PENDING',
  pendingAIP: 'PENDING-AIP',
  upcoming: 'UPCOMING',
  running: 'RUNNING',
  completed: 'COMPLETED',
  deactivated: 'DEACTIVATED',
};

export const COMMON_ES_FIELDS = {
  score: '_score',
  updatedAt: 'updatedAt',
};

export const PROJECT_ES_FIELDS = {
  start: 'start',
  end: 'end',
  updatedAt: COMMON_ES_FIELDS.updatedAt,
  score: COMMON_ES_FIELDS.score,
  countryID: 'countryID',
  creator: 'creator',
  status: 'status',
  audience: 'audience',
  objective: 'objective',
  name: 'name',
  shortName: 'shortName',
  id: 'ID',
};

export const CAMPAIGN_ES_FIELDS = {
  start: 'start',
  end: 'end',
  updatedAt: COMMON_ES_FIELDS.updatedAt,
  countryID: 'countryID',
  creator: 'creator',
  status: 'status',
  verticals: 'verticals',
  audience: 'audience',
  channels: 'channels',
  channelGroup: 'channelGroup',
  projectID: 'projectID',
  name: 'name',
  code: 'code',
  shortName: 'shortName',
  templateType: 'templateType',
  approver: 'approver',
};

export const CAMPAIGN_QUERY_KEYS = {
  country: 'campaign_country',
  page: 'campaign_page',
  pageSize: 'campaign_page_size',
  status: 'campaign_status',
  ownership: 'campaign_ownership',
  pendingApproval: 'campaign_pending_approval',
  vertical: 'campaign_vertical',
  moreFilters: 'campaign_more_filters',
  search: 'campaign_search',
};

export const VERTICALS = {
  transport: 'transport',
  food: 'food',
  mart: 'mart',
  express: 'express',
  finance: 'finance',
  oneGrab: 'oneGrab',
  challenges: 'challenges',
  gfb: 'gfb',
  kitchen: 'kitchen',
  rentals: 'rentals',
  rewards: 'rewards',
  grabUnlimited: 'grabUnlimited',
  omnicommerce: 'omnicommerce',
  digitalGoods: 'digitalGoods',
  brand: 'brand',
};

export const BUDGET_SOURCES = {
  newBiz: 'newBiz',
  paymentRetention: 'paymentRetention',
  paymentAcquisition: 'paymentAcquisition',
  oneGrabRetention: 'oneGrabRetention',
  oneGrabAcquisition: 'oneGrabAcquisition',
  martRetention: 'martRetention',
  martAcquisition: 'martAcquisition',
  foodRetention: 'foodRetention',
  foodAcquisition: 'foodAcquisition',
  transportRetention: 'transportRetention',
  transportAcquisition: 'transportAcquisition',
};

export const BUDGET_SOURCE_DISPLAY_NAMES = {
  newBiz: 'New Biz',
  paymentRetention: 'Payment Retention',
  paymentAcquisition: 'Payment Acquisition',
  oneGrabRetention: 'One Grab Retention',
  oneGrabAcquisition: 'One Grab Acquisition',
  martRetention: 'Mart Retention',
  martAcquisition: 'Mart Acquisition',
  foodRetention: 'Food Retention',
  foodAcquisition: 'Food Acquisition',
  transportRetention: 'Transport Retention',
  transportAcquisition: 'Transport Acquisition',
  expressRetention: 'Express Retention',
  expressAcquisition: 'Express Acquisition',
};

export const VERTICALS_DISPLAY_NAMES = {
  transport: 'Transport',
  food: 'Food',
  mart: 'Mart',
  express: 'Express',
  finance: 'Finance',
  digibank: 'Digibank',
  oneGrab: 'One Grab',
  challenges: 'Challenges',
  gfb: 'GFB',
  kitchen: 'Kitchen',
  rentals: 'Rentals',
  rewards: 'Rewards',
  grabUnlimited: 'GrabUnlimited',
  jayaGrocer: 'Jaya Grocer',
  omnicommerce: 'Omnicommerce',
  digitalGoods: 'Digital Goods',
  brand: 'Brand',
};

export const CHANNEL_GROUPS_DESCRIPTION = {
  pm: 'Serve paid advertising to your audience based on specific criteria with third party channels',
  hedwig:
    'Trigger in-app pop-ups at the start or end of a customer journey (Food, Mart Tile etc)',
  trident:
    'Trigger actions by sending comms, awarding rewards or points in real-time based on events',
  feed: "Create ads, dynamic content and manually curated content that appear on the Grab app's homescreen",
  salesforce:
    'Build and manage personalised customer journeys across channels and devices',
};

export const CHANNEL_GROUPS = {
  feed: 'feed',
  trident: 'trident',
  hedwig: 'hedwig',
  pm: 'pm',
  salesforce: 'salesforce',
};

export const CHANNEL_GROUP_DISPLAY_NAMES = {
  pm: 'Performance Marketing',
  hedwig: 'Hedwig',
  trident: 'Trident',
  feed: 'In-App Feed',
  salesforce: 'Salesforce',
};

export const CHANNEL_GROUP_CHANNELS = {
  pm: [
    'google',
    'facebook',
    'mcSaatchi',
    'dv360',
    'tradeDesk',
    'affiliates',
    'directBuy',
    'otherPaid',
  ],
  hedwig: ['inApp'],
  trident: ['email', 'push', 'pci', 'inbox', 'sms'],
  feed: ['feed'],
  salesforce: ['email', 'push', 'pci'],
};

export const CHANNEL_GROUP_ENABLED = {
  feed: false,
  trident: true,
  hedwig: false,
  pm: false,
  salesforce: false,
};

export const CHANNEL_DISPLAY_NAMES = {
  google: 'Google',
  facebook: 'Facebook',
  mcSaatchi: 'McSaatchi',
  dv360: 'DV360',
  tradeDesk: 'TradeDesk',
  affiliates: 'Affiliates',
  directBuy: 'DirectBuy',
  otherPaid: 'Other Paid Channels',
  inApp: 'In-App Pop-Up',
  email: 'Email',
  push: 'Push Notifications',
  inbox: 'Inbox',
  pci: 'Push Chained Inbox',
  sms: 'SMS',
  feed: 'Feed',
};

export const CHANNEL_KEYS = {
  email: 'Email',
  push: 'Push',
  pci: 'Push Chained Inbox',
  pciInbox: 'PCI - Inbox',
  pciPush: 'PCI - Push',
};

export const CAMPAIGN_DEFAULT_PAGE_SIZE = 12;
export const PROJECT_DEFAULT_PAGE_SIZE = 12;

export const ADD_NEW_MODAL = 'addNewModal';
export const ADD_NEW_PROJECT = 'addNewProject';
export const ADD_NEW_CAMPAIGN = 'addNewCampaign';

export const UCD_ROUTES_LINK = {
  CAMPAIGN_DUPLICATE: (projectID, campaignID) =>
    `/ucd/project/${projectID}/campaign/new?campaignToDuplicate=${campaignID}`,
  CAMPAIGN_EDIT: (projectID, campaignID) =>
    `/ucd/project/${projectID}/campaign/${campaignID}/edit`,
  CAMPAIGN_VIEW: (projectID, campaignID) =>
    `/ucd/project/${projectID}/campaign/${campaignID}/view`,
  HOME: () => '/ucd',
  JOURNEY_CAMPAIGN_GROUP_CREATE: () => '/ucd/journey-campaign-group/create',
  JOURNEY_CAMPAIGN_GROUP_EDIT: (campaignGroupID) =>
    `/ucd/journey-campaign-group/${campaignGroupID}/edit`,
  JOURNEY_CAMPAIGN_GROUP_VIEW: (campaignGroupID) =>
    `/ucd/journey-campaign-group/${campaignGroupID}/view`,
  JOURNEY_CAMPAIGN_CREATE: (projectID, campaignID, template) =>
    `/ucd/project/${projectID}/journey_campaign/${campaignID}/create/${template}`,
  JOURNEY_CAMPAIGN_BUILDER_EDIT: (projectID, campaignID) =>
    `/ucd/project/${projectID}/campaign/${campaignID}/editJourney`,
  JOURNEY_CAMPAIGN_EDIT: (projectID, campaignID) =>
    `/ucd/project/${projectID}/journey_campaign/${campaignID}/edit`,
  JOURNEY_CAMPAIGN_VIEW: (projectID, campaignID) =>
    `/ucd/project/${projectID}/journey_campaign/${campaignID}/view`,
  PROJECT_CREATE: () => '/ucd/project/new',
  PROJECT_DUPLICATE: (projectID) =>
    `/ucd/project/new?projectToDuplicate=${projectID}`,
  PROJECT_EDIT: (projectID) => `/ucd/project/${projectID}/edit`,
  PROJECT_VIEW: (projectID) => `/ucd/project/${projectID}/view`,
  SMART_CAMPAIGN_CREATE: () => '/ucd/smart-comms/create',
  SMART_CAMPAIGN_EDIT: (projectID, campaignID) =>
    `/ucd/project/${projectID}/campaign/${campaignID}/smart-comms/edit`,
  SMART_CAMPAIGN_DUPLICATE: (projectID, campaignID) =>
    `/ucd/project/${projectID}/smart-comms/create?${QUERY_PARAM_KEYS.campaignToDuplicate}=${campaignID}`,
  SMART_CAMPAIGN_VIEW: (projectID, campaignID) =>
    `/ucd/project/${projectID}/campaign/${campaignID}/smart-comms/view`,
  TRIDENT_CAMPAIGN_CREATE: (projectID, campaignID) =>
    `/ucd/project/${projectID}/campaign/${campaignID}/trident/create`,
  TRIDENT_CAMPAIGN_EDIT: (projectID, campaignID) =>
    `/ucd/project/${projectID}/campaign/${campaignID}/trident/edit`,
};

export const CHANNEL_GROUP_ICONS_MAP = {
  [CHANNEL_GROUPS.pm]: pmIcon,
  [CHANNEL_GROUPS.hedwig]: hedwigIcon,
  [CHANNEL_GROUPS.trident]: tridentIcon,
  [CHANNEL_GROUPS.feed]: feedIcon,
  [CHANNEL_GROUPS.salesforce]: salesforceIcon,
};

export const OBJECTIVES = {
  userAcquisition: 'userAcquisition',
  userActivation: 'userActivation',
  userRetention: 'userRetention',
  userReactivation: 'userReactivation',
  demandGeneration: 'demandGeneration',
  branding: 'branding',
  crossSell: 'crossSell',
  communications: 'communications',
  education: 'education',
  funnelImprovement: 'funnelImprovement',
  newProductAwareness: 'newProductAwareness',
  subscription: 'subscription',
  payments: 'payments',
  rewards: 'rewards',
};

export const QUERY_PARAM_KEYS = {
  previousPageIsListing: 'previousPageIsListing',
  campaignToDuplicate: 'campaignToDuplicate',
  copyContent: 'copyContent',
};

export const SUB_OBJECTIVES = {
  crossSell: {
    label: 'Cross-sell',
  },
  growAvgTxnFreq: {
    label: 'Grow Avg. Transaction Frequency',
  },
  growAvgTxnValue: {
    label: 'Grow Avg. Transaction Size',
  },
  growTxnUsers: {
    label: 'Grow Number of Transacting User',
  },
};

export const OBJECTIVES_ICON_LABEL = {
  [OBJECTIVES.userAcquisition]: {
    label: 'Acquire New-to-Grab Users',
    icon: userAcquisitionIcon,
  },
  [OBJECTIVES.userActivation]: {
    label: 'Activate New Users',
    icon: userAcquisitionIcon,
  },
  [OBJECTIVES.userRetention]: {
    label: 'Retain Lapsing Users',
    icon: userRetentionIcon,
  },
  [OBJECTIVES.userReactivation]: {
    label: 'Reactivate Churned Users',
    icon: userReactivationIcon,
  },
  [OBJECTIVES.demandGeneration]: {
    label: 'Demand Generation',
    icon: demandGenerationIcon,
  },
  [OBJECTIVES.crossSell]: {
    label: 'Cross-sell',
    icon: crossSellIcon,
  },
  [OBJECTIVES.branding]: { label: 'Brand Awareness', icon: brandAwarenessIcon },
  [OBJECTIVES.communications]: {
    label: 'General Announcements',
    icon: communicationsIcon,
  },
  [OBJECTIVES.education]: { label: 'Education', icon: brandAwarenessIcon },
  [OBJECTIVES.funnelImprovement]: {
    label: 'Funnel Improvement',
    icon: brandAwarenessIcon,
  },
  [OBJECTIVES.newProductAwareness]: {
    label: 'New Product/CVP Awareness',
    icon: brandAwarenessIcon,
  },
  [OBJECTIVES.subscription]: {
    label: 'Subscription (Horizontal)',
    icon: brandAwarenessIcon,
  },
  [OBJECTIVES.payments]: {
    label: 'Payments (Horizontal)',
    icon: brandAwarenessIcon,
  },
  [OBJECTIVES.rewards]: {
    label: 'Rewards (Horizontal)',
    icon: brandAwarenessIcon,
  },
};

export const FILTER_FIELDS = {
  myCampaign: 'myCampaign',
  pendingApproval: 'pendingApproval',
  country: 'country',
  vertical: 'vertical',
  status: 'status',
  projectStatus: 'projectStatus',
};

const urlRegex =
  /^(?:(?:https?|ftp):\/\/)?(?:(?!(?:10|127)(?:\.\d{1,3}){3})(?!(?:169\.254|192\.168)(?:\.\d{1,3}){2})(?!172\.(?:1[6-9]|2\d|3[0-1])(?:\.\d{1,3}){2})(?:[1-9]\d?|1\d\d|2[01]\d|22[0-3])(?:\.(?:1?\d{1,2}|2[0-4]\d|25[0-5])){2}(?:\.(?:[1-9]\d?|1\d\d|2[0-4]\d|25[0-4]))|(?:(?:[a-z\u00a1-\uffff0-9]-*)*[a-z\u00a1-\uffff0-9]+)(?:\.(?:[a-z\u00a1-\uffff0-9]-*)*[a-z\u00a1-\uffff0-9]+)*(?:\.(?:[a-z\u00a1-\uffff]{2,})))(?::\d{2,5})?(?:\/\S*)?$/;
export const URL_REGEX = new RegExp(urlRegex);

// concedo resource name
export const UCD_RESOURCE_NAMES = {
  campaign: 'ucd_campaign',
  project: 'ucd_project',
  contentConfig: 'ucd_content_config',
};

// concedo resource field
export const UCD_RESOURCE_FIELDS = {
  all: 'ALL',
};

// concedo action types
export const UCD_ACTION_TYPES = {
  create: 'create',
  read: 'read',
  update: 'update',
  active: 'active',
};

export const UCD_ERRORS = {
  PROJECT_CODE_EXIST: {
    errorMessage: 'project already exists with the given code',
    alertMessage:
      'Project code already exists. Please change the project name.',
  },
  CAMPAIGN_CODE_EXIST: {
    errorMessage: 'campaign already exists with the given code',
    alertMessage:
      'Campaign code already exists. Please change the campaign name.',
  },
};

/**
 * Column field config
 * @param {string} key
 * @param {string} title
 * @param {string: default type | 'currency' | 'number'} type
 * @param {object: true | false | function } sorter
 */
const field = (key, title, type, sorter = true) => {
  const defaultSort = (a, b) => a[key] - b[key];
  return {
    key,
    title,
    type,
    sorter: (() => {
      if (sorter === undefined || sorter === false) return false;
      return _.isFunction(sorter) ? sorter : defaultSort;
    })(),
  };
};

export const FIELD_TYPE = {
  CURRENCY: 'currency',
  NUMBER: 'number',
};

export const NUMBER_FORMAT = {
  INTEGER: '0,0',
  CURRENCY: '0,0.00',
};

// PROJECT_OBJECTIVE_COLUMNS
const POC = {
  NEW_USERS: field('newUsers', 'New Users', FIELD_TYPE.NUMBER),
  GMV_USD: field('gmv', 'GMV (USD)', FIELD_TYPE.CURRENCY),
  TRANSACTIONS: field('transactions', 'Transactions', FIELD_TYPE.NUMBER),
  REVENUE_USD: field('revenue', 'Revenue (USD)', FIELD_TYPE.CURRENCY),
  RETAINED_USERS: field('retainedUsers', 'Retained Users', FIELD_TYPE.NUMBER),
  REACTIVATED_USERS: field(
    'reactivatedUsers',
    'Reactivated Users',
    FIELD_TYPE.NUMBER,
  ),
  NEW_TO_VERTICAL_USERS: field(
    'newToVerticalUsers',
    'New-to-vertical Users',
    FIELD_TYPE.NUMBER,
  ),
  AVG_TXN_FREQUENCY: field(
    'avgTxnFrequency',
    'Avg Trx Frequency',
    FIELD_TYPE.NUMBER,
  ),
  AVG_ORDER_VALUE_USD: field(
    'avgOrderValue',
    'Avg Order Value (USD)',
    FIELD_TYPE.CURRENCY,
  ),
  TRANSACTED_USERS: field(
    'transacted_users',
    'Transacted Users',
    FIELD_TYPE.NUMBER,
  ),
  ACTIVE_USERS: field('activeUsers', 'Active Users', FIELD_TYPE.NUMBER),
};

/**
 * Consider to move it to UCM
 */
export const PROJECT_OBJECTIVE_COLUMNS_MAPPING = {
  userAcquisition: [
    POC.NEW_USERS,
    POC.GMV_USD,
    POC.TRANSACTIONS,
    POC.REVENUE_USD,
  ],
  userRetention: [
    POC.RETAINED_USERS,
    POC.GMV_USD,
    POC.TRANSACTIONS,
    POC.REVENUE_USD,
  ],
  userReactivation: [
    POC.REACTIVATED_USERS,
    POC.GMV_USD,
    POC.TRANSACTIONS,
    POC.REVENUE_USD,
  ],
  branding: [POC.GMV_USD],
  demandGeneration: [POC.GMV_USD, POC.TRANSACTIONS, POC.REVENUE_USD],
  crossSell: [
    POC.NEW_TO_VERTICAL_USERS,
    POC.GMV_USD,
    POC.TRANSACTIONS,
    POC.REVENUE_USD,
  ],
  communications: [],
};

// Key: Field on js Field.  Value: Field from API response.
export const METRICS_FIELD_MAPPING = {
  pci: {
    push: {
      triggered: 'triggered',
      sent: 'pci.push.sent',
      notSent: 'pci.push.notSent',
      delivered: 'pci.push.delivered',
      open: 'pci.push.open',
    },
    inbox: {
      triggered: 'triggered',
      sent: 'pci.inbox.sent',
      notSent: 'pci.inbox.notSent',
      delivered: 'pci.inbox.delivered',
      open: 'pci.inbox.open',
      click: 'click',
    },
  },
  default: {
    triggered: 'triggered',
    sent: 'sent',
    notSent: 'notSent',
    delivered: 'delivered',
    open: 'open',
    click: 'click',
    attempted: 'attempted', // calculated manually:  sent + notSent
    undelivered: 'undelivered', // calculated manually:  sent - delivered
  },
};

export const METRICS_RATES = {
  sent: 'rates.sent',
  deliverability: 'rates.deliverability',
  open: 'rates.open',
  click: 'rates.click',
};

export const METRICS_CELL_ITEMS = {
  val: 'val',
  percentage: 'percentage',
  uplift: 'uplift',
  hide: 'hide',
  benchmarkScore: 'benchmarkScore', // e.g: Bad, Good, Great
  benchmarkRate: 'benchmarkRate',
};

export const CAMPAIGN_METRICS_FORMULA_PROPS = {
  OPEN_RATE: 'openRate',
  NOTIF_CLICK_RATE: 'notifclickRate',
  CTA_CLICK_RATE: 'ctaclickRate',
};

export const CAMPAIGN_METRICS_CHART_CONFIG = {
  pushinbox: {
    displayText: 'PCI',
    metrics: {
      sent: { text: 'Sent', bg: colorBrandPrimaryDefault },
      received: { text: 'Received', bg: colorBrandPrimaryDefault },
      notifclick: { text: 'Clicked', bg: colorBrandPrimaryDefault },
      open: { text: 'Opened', bg: colorBrandPrimarySoft },
      ctaclick: { text: 'Clicked', bg: colorBrandPrimarySoft },
    },
    rates: {
      notifclickRate: {
        lhs: 'notifclick',
        rhs: 'received',
        text: 'Open Rate (Push)',
      },
      openRate: { lhs: 'open', rhs: 'sent', text: 'Open Rate (Inbox)' },
      ctaclickRate: {
        lhs: 'ctaclick',
        rhs: 'notifclick',
        text: 'Click-Through Rate',
      },
    },
  },
  push: {
    displayText: 'PN',
    metrics: {
      triggered: { text: 'Triggered', bg: colorBrandPrimaryDefault },
      sent: { text: 'Sent', bg: colorStatusPositiveBold },
      notsent: { text: 'Not Sent', bg: colorBrandPrimaryDefault },
      delivered: { text: 'Delivered', bg: colorBrandPrimaryDefault },
      open: { text: 'Opened', bg: colorBrandPrimaryDefault },
    },
    rates: {
      notifclickRate: { lhs: 'notifclick', rhs: 'received', text: 'Open Rate' },
    },
  },
  inbox: {
    displayText: 'Inbox',
    metrics: {
      sent: { text: 'Sent', bg: colorBaseBlue300 },
      open: { text: 'Opened', bg: colorBaseBlue300 },
      ctaclick: { text: 'Clicked', bg: colorBaseBlue300 },
    },
    rates: {
      openRate: { lhs: 'open', rhs: 'sent', text: 'Open Rate' },
      ctaclickRate: {
        lhs: 'ctaclick',
        rhs: 'open',
        text: 'Click-Through Rate',
      },
    },
  },
  email: {
    displayText: 'Email',
    metrics: {
      sent: { text: 'Sent', bg: colorStatusInformativeBold },
      received: { text: 'Received', bg: colorStatusInformativeBold },
      open: { text: 'Opened', bg: colorStatusInformativeBold },
      ctaclick: { text: 'Clicked', bg: colorStatusInformativeBold },
    },
    rates: {
      openRate: { lhs: 'open', rhs: 'received', text: 'Open Rate' },
      ctaclickRate: {
        lhs: 'ctaclick',
        rhs: 'open',
        text: 'Click-Through Rate',
      },
    },
  },
  inapp: {
    displayText: 'In-app Pop-up',
    metrics: {
      received: { text: 'Received', bg: colorBrandSecondaryDefault },
      notifclick: { text: 'Clicked', bg: colorBrandSecondaryDefault },
    },
    rates: {
      ctaclickRate: {
        lhs: 'notifclick',
        rhs: 'received',
        text: 'Click-Through Rate',
      },
    },
  },
};

export const CAMPAIGN_TYPES = {
  trident: 'trident',
  journey: 'journey',
};

export const TRIDENT_CAMPAIGN_TEMPLATE = {
  simpleSchedule: 'simple-schedule',
  simpleScenario: 'simple-scenario',
  multistepJourney: 'multi-step-journey',
  fromBlank: 'from-blank',
  smartSchedule: 'smart-schedule',
};

export const TRIDENT_CAMPAIGN_TEMPLATE_DISPLAY_NAMES = {
  simpleSchedule: 'Simple Schedule',
  simpleScenario: 'Simple Scenario',
  multistepJourney: 'Multi-step Journey',
  fromBlank: 'From Blank',
  smartSchedule: 'Smart Schedule',
};

export const TRIDENT_CAMPAIGN_PROGRESS_PLACEHOLDER = {
  template: {
    title: 'Create Trident Template',
    text: 'Select Template',
  },
  enumeration: {
    title: 'Set-up Audience and Action',
    text: 'Select segment that suits the audience.',
  },
  schedule: {
    title: 'Schedule and Approval',
    text: 'Best date and time to run this campaign.',
  },
  scenario: {
    title: 'Select Event and Action',
    text: 'Events and actions suits the campaign.',
  },
  select_journey_template: {
    title: 'Select Journey Template',
    text: 'Select the template based on your use case',
  },
  customize_journey: {
    title: 'Customise Journey',
    text: 'Customise the content and conditions',
  },
  approval: {
    title: 'Submit for Approval',
  },
};

export const TRIDENT_CAMPAIGN_TEMPLATE_TYPES = [
  {
    title: 'Simple Schedule',
    value: 'simple-schedule',
    description:
      'Create an action triggered upon a time schedule based on the selected channel',
    icon: simpleScheduleIcon,
    display: true,
  },
  {
    title: 'Simple Scenario',
    value: 'simple-scenario',
    description: 'Create actions that are triggered upon a real-time event',
    icon: simpleScenarioIcon,
    display: true,
  },
  {
    title: 'Multi-step Journey',
    value: 'multi-step-journey',
    description:
      'Create actions to be triggered for your selected users based on real-time events that you set',
    icon: multistepJourneyIcon,
    display: true,
  },
  // TODO: Add 'smartSchedule'
];

export const TRIDENT_CAMPAIGN_PROGRESS_DETAIL_FIELDS = {
  fieldName: 'name',
  fieldValue: 'value',
  fieldUrl: 'url',
};

export const TRIDENT_OWNERS = [
  {
    key: 'TARGETING',
    text: 'Targeting',
    value: 'TARGETING',
  },
  {
    key: 'quest',
    text: 'Quest',
    value: 'quest',
  },
];

export const LIMIT_ITEMS = [
  {
    label: 'Total Limit',
    inputKey: 'limit.total',
    checkboxKey: 'limit.isTotalUnlimited',
  },
  {
    label: 'Total Per User',
    inputKey: 'limit.totalPerUser',
    checkboxKey: 'limit.isTotalPerUserUnlimited',
  },
  {
    label: 'Total Limit Daily',
    inputKey: 'limit.totalDaily',
    checkboxKey: 'limit.isTotalDailyUnlimited',
  },
  {
    label: 'Total Per User Daily',
    inputKey: 'limit.totalPerUserDaily',
    checkboxKey: 'limit.isTotalPerUserDailyUnlimited',
  },
];

export const METRICS_TO_RATE_NAME_MAPPING = {
  open: 'openRate',
  click: 'clickThroughRate',
};

export const SERVICE_NAMES = {
  MYSTIQUE: 'MYSTIQUE',
  HEDWIG: 'HEDWIG',
};

export const PRODUCT_OVERVIEW_MAP = {
  transport:
    "Grab's Transport service offers a convenient and reliable ride-hailing solution, ensuring you reach your destination safely and on time.",
  food: 'With a few taps, you can have your cravings delivered to your doorstep.',
  mart: 'GrabMart delivers convenience items such as groceries and daily essentials on-demand. ',
  express:
    'GrabExpress is a premier courier service that guarantees swift and secure delivery of parcels and documents across the region. With features like real-time tracking and proof of delivery, it ensures a hassle-free delivery experience.',
  finance:
    'GrabPay is a comprehensive digital wallet solution that simplifies your daily transactions. It integrates seamlessly with all Grab services and numerous partner platforms, making it a versatile payment option.',
  oneGrab:
    'One Grab is your all-in-one app, integrating all Grab services for a seamless user experience. From transportation and food delivery to payments and parcel delivery, it offers a broad spectrum of services, making it a one-stop solution for all your needs.',
  challenges: '',
  gfb: 'Grab for Business is a comprehensive solution that caters to corporate transportation and delivery needs. It offers businesses a simple and efficient way to manage their transport and delivery expenses, ensuring transparency and control.',
  kitchen:
    'GrabKitchen is a cloud kitchen platform that houses multiple food brands in a single central kitchen. It allows for a variety of food options to be prepared and delivered from one location, increasing efficiency and choice.',
  rentals:
    'GrabRentals is the largest private-hire car rental fleet in Singapore, directly owned by Grab. We provide one-stop car rental for a variety of car models for petrol, hybrid and EVs at affordable rates to suit your driving needs.',
  rewards:
    'GrabRewards is your loyalty program, rewarding you for every transaction with exciting benefits.',
  grabUnlimited:
    'GrabUnlimited is a paid membership that unlocks discounts across all of Grab services, which include food delivery, dine out deals, transport and more. Members get unlimited free delivery from any restaurant, any day, and exclusive partner benefits.',
};

export const REASON_TO_USE_MAP = {
  transport:
    'Avoid the hassle of driving and parking. Enjoy a safe and comfortable ride with professional drivers.',
  food: 'GrabFood provides the widest range of restaurants for all cravings and occasion and exceptional food delivery service. Plus our Saver delivery option, GrabUnlimited subscription and Food deals are just some ways we help you scrimp on costs, but not on taste.',
  mart: 'Browse thousands of stores - from specialty grocers to convenience stores, and pharmacies to pet shops. Get what you need conveniently delivered at anytime of the day or night. Flexibile timings, depending on how urgent your need; choose from instant delivery or scheduled. ',
  express:
    "It offers a fast, secure, and trackable service, providing you with peace of mind whether you're sending personal items or business parcels. Choose from a range of vehicle types, depending on the size of your item. Track your parcel in real time. Enjoy upfront fixed fares and cashless payments. Experience competitive pricing for same day parcel delivery service. Your items are handled by our well-trained delivery fleet.",
  finance:
    'A secure and convenient payment solution. It streamlines your transactions with Grab services and a wide array of partners, earning you rewards with every transaction.',
  oneGrab:
    'Experience the convenience of having transport, food delivery, payments, and more in one platform to save you time and effort.',
  challenges: '',
  gfb: 'Opt for Grab for Business for a streamlined and efficient management of your corporate transportation and delivery needs. It provides transparency, control, and convenience, making business operations smoother.',
  kitchen:
    'Choose GrabKitchen for a diverse food experience. It offers a wide range of cuisines from multiple brands, all prepared in a single location and delivered fresh to your doorstep. Pay only one delivery fee, and get to taste the world in one place.',
  rentals: `"Experience a hassle-free rental journey with GrabRentals, where you'll enjoy exclusive perks such as free rental days, completion bonus & more when you rent with us. As a trusted rental company directly under Grab, we provide personalised support to ensure you drive with peace of mind. Visit us at Grab@Tampines for dedicated assistance tailored to your needs.

In addition, you'll benefit from up to 31% fuel discounts, and all your servicing and maintenance needs are covered for free at GrabRentals' trusted workshops."`,
  rewards:
    'Make every transaction count by earning GrabRewards points and redeem them for a wide range of rewards.',
  grabUnlimited: `Savings angle: Start saving as long as you make 2 monthly deliveries. Average member saves $x per month ($x differs by country).
Exclusive benefits angle: Enjoy exclusive perks from partners across F&B, travel, OTT and more.`,
};

export const CALL_TO_ACTION_MAP = {
  transport: 'Learn More\nFind Out More\nBook Now',
  food: 'Learn More\nFind Out More\nOrder Now',
  mart: 'Learn More\nFind Out More\nOrder Now',
  express: 'Learn More\nFind Out More\nBook Now',
  finance:
    'Learn More\nFind Out More\nPay Now\nLink Now\nBook Now\nOrder Now\nActivate Now',
  oneGrab:
    'Learn More\nFind Out More\nActivate Now\nCheck Out My Rewards\nBook Now\nOrder Now\nSubscribe Now',
  challenges: '',
  gfb: 'Learn More\nFind Out More\nActivate Now',
  kitchen: 'Learn More\nFind Out More',
  rentals: 'Learn More\nFind Out More\nRent Now',
  rewards:
    'Learn More\nFind Out More\nActivate Now\nCheck Out My Rewards\nBook Now\nOrder Now',
  grabUnlimited:
    'Learn More\nSubscribe Now\n(New to GU - free trial) Try it for free\n(On GU already, but upgrading to longer term plans) Upgrade your plan',
};

export const UCD_MYSTIQUE_CHANNEL_NAMES = {
  push: 'Push Notification',
  pci: 'Push Chained Inbox',
};

/**
 * 1 Malaysia
 * 2 Philippines
 * 3 Thailand
 * 4 Singapore
 * 5 Vietnam
 * 6 Indonesia
 * 11 France
 * 30 Brazil
 * 31 Canada
 * 41 Germany
 * 46 Hong Kong
 * 50 Italy
 * 51 Japan
 * 58 Mexico
 * 59 Netherlands
 * 60 New Zealand
 * 73 Spain
 * 76 Switzerland
 * 79 Turkey
 * 8 United States
 * 9 Myanmar
 * 10 Cambodia
 */
export const LANGUAGE_CODE_BASED_ON_COUNTRY_ID = {
  1: 'en',
  2: 'en',
  3: 'th',
  4: 'en',
  5: 'vi',
  6: 'id',
  11: 'en',
  30: 'en',
  31: 'en',
  41: 'en',
  46: 'en',
  50: 'en',
  51: 'en',
  58: 'en',
  59: 'en',
  60: 'en',
  73: 'en',
  76: 'en',
  79: 'en',
  8: 'en',
  9: 'my',
  10: 'km',
  77: 'zh-Hant-TW',
};

export const COUNTRY_ID_VS_COUNTRY_CODE = {
  1: 'MY',
  2: 'PH',
  3: 'TH',
  4: 'SG',
  5: 'VN',
  6: 'ID',
  11: 'FR',
  30: 'BR',
  31: 'CA',
  41: 'DE',
  46: 'HK',
  50: 'IT',
  51: 'JP',
  58: 'MX',
  59: 'NL',
  60: 'NZ',
  73: 'ES',
  76: 'CH',
  79: 'TR',
  8: 'US',
  9: 'MM',
  10: 'KH',
  77: 'TW',
};

export const COUNTRY_ID_VS_COUNTRY_NAME = {
  1: 'Malaysia',
  2: 'Philippines',
  3: 'Thailand',
  4: 'Singapore',
  5: 'Vietnam',
  6: 'Indonesia',
  11: 'France',
  30: 'Brazil',
  31: 'Canada',
  41: 'Germany',
  46: 'Hong Kong',
  50: 'Italy',
  51: 'Japan',
  58: 'Mexico',
  59: 'Netherlands',
  60: 'New Zealand',
  73: 'Spain',
  76: 'Switzerland',
  79: 'Turkey',
  8: 'United States',
  9: 'Myanmar',
  10: 'Cambodia',
  77: 'Taiwan',
};

export const SMART_CAMPAIGN_FORM_KEYS = {
  CAMPAIGN_NAME: 'campaignName',
  VERTICAL: 'vertical',
  CVP: 'cvp',
  START_DATE: 'startDate',
  END_DATE: 'endDate',
  CONTROL_GROUP_TIME: 'controlGroupTime',
  SEGMENT_NAMESPACE: 'namespace',
  SEGMENTS: 'segments',
  CAMPAIGN_CONTENT: 'campaignContent',
  CHANNEL: 'channel',
  APPROVER: 'approver',
  VALUE_PROPOSITION: 'valueProposition',
  TOPIC: 'topic',
  FEEDBACK: 'feedback',
  PROJECT: 'project',
  CALL_TO_ACTIONS: 'callToAction',
  PRODUCT_OVERVIEW: 'productOverview',
  REASON_TO_USE: 'reasonToUse',
  JTBD: 'JTBD',
  KEY_POINTER: 'keyPointer',
  TONE: 'tone',
  PN_IMAGE_URL: 'pnImageUrl',
  SEGMENT_SIZE: 'segmentSize',
  SLANG_TOGGLE: 'slangToggle',
  CONTENT_SOURCE: 'contentSource',

  ACTION_TYPE: 'actionType',
  ACTION_URL: 'actionURL',

  CAMPAIGN_CONTENT_TITLE: 'campaignContentTitle',
  CAMPAIGN_CONTENT_SLANG: 'campaignContentSlang',
  CAMPAIGN_CONTENT_MESSAGE: 'campaignContentMessage',
  CAMPAIGN_CONTENT_INITIAL_VALUES: 'campaignContentInitialValues',

  PCI_CATEGORY: 'inboxCategory',
  PCI_TITLE: 'inboxTitle',
  PCI_SUBTITLE: 'inboxSubtitle',
  PCI_COVER_IMAGE: 'pci.coverImage',
  PCI_MESSAGE: 'inboxMessage',
  PCI_BUTTON_TEXT: 'inboxButtonText',
  PCI_BUTTON_LINK: 'inboxButtonLink',
  PCI_COVER_IMAGE_URL: 'inboxCoverImageUrl', // To save image url - doesn't have any field in form

  NEW_CALL_TO_ACTION: 'newCallToAction', // name of input box to add new 'call to action'

  DYNAMIC_ATTRIBUTE: 'dynamicAttribute',

  PREVIEW_MESSAGE_PAX_IDS: 'previewMessagePaxIds',

  GMS_ENABLED: 'isGMSEnabled', // To save if the campaign is a GMS campaign

  IS_RECURRING_CAMPAIGN: 'isRecurrenceEnabled', // To save if the campaign is a recurring campaign
  RECURRENCE_WINDOW_DAYS: 'customRecurrenceWindowInDays', // Custom recurrence window in days

  TIME_WINDOW: 'timeWindow',

  IS_TIME_SENSITIVE: 'isTimeSensitive', // To save if the campaign is time sensitive

  AUDIENCE_TYPE: 'audienceType', // Target audience type (Locals or Travellers)
};

export const SMART_CAMPAIGN_FORM_FIELD_LABELS: Record<string, string> = {
  [SMART_CAMPAIGN_FORM_KEYS.CAMPAIGN_NAME]: 'Campaign Name',
  [SMART_CAMPAIGN_FORM_KEYS.PROJECT]: 'Project',
  [SMART_CAMPAIGN_FORM_KEYS.VERTICAL]: 'Vertical',
  [SMART_CAMPAIGN_FORM_KEYS.SEGMENTS]: 'Segments',
  [SMART_CAMPAIGN_FORM_KEYS.START_DATE]: 'Start Date',
  [SMART_CAMPAIGN_FORM_KEYS.END_DATE]: 'End Date',
  [SMART_CAMPAIGN_FORM_KEYS.CHANNEL]: 'Format',
  [SMART_CAMPAIGN_FORM_KEYS.ACTION_URL]: 'Action URL',
  [SMART_CAMPAIGN_FORM_KEYS.PCI_TITLE]: 'Inbox Title',
  [SMART_CAMPAIGN_FORM_KEYS.PCI_MESSAGE]: 'Inbox Message',
  [SMART_CAMPAIGN_FORM_KEYS.PCI_CATEGORY]: 'Inbox Category',
};

export const SEGMENT_NAMESPACE_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'shared', label: 'Shared' },
  { value: 'ads', label: 'Ads' },
];

export const SMART_CAMPAIGN_CHANNELS = {
  push: 'push',
  pci: 'pci',
};

export const SMART_CAMPAIGN_CHANNEL_NAME_BASED_ON_HEDWIG = {
  push: 'push',
  pci: 'pushinbox',
};

export const CREATE_SMART_CAMPAIGN_DEFAULTS = {
  channels: [SMART_CAMPAIGN_CHANNELS.push],
  channelGroup: 'trident',
  budgetSource: 'foodAcquisition', // need to confirm
  templateType: TRIDENT_CAMPAIGN_TEMPLATE.smartSchedule,
  inboxCategory: 'offers',
  tone: 'Grab voice',
};

export const SMART_CAMPAIGN = 'smart-campaign';
export const MANUAL_CAMPAIGN = 'manual-campaign';
export const PROJECT = 'project';
export const JOURNEY_CAMPAIGN_GROUP = 'journey-campaign-group';

export const channelOptions = [
  // { value: 'email', label: CHANNEL_DISPLAY_NAMES['email'] },
  {
    value: SMART_CAMPAIGN_CHANNELS.push,
    label: UCD_MYSTIQUE_CHANNEL_NAMES.push,
    mystiqueContentType: UCD_MYSTIQUE_CHANNEL_NAMES.push,
    hedwigContentType: SMART_CAMPAIGN_CHANNEL_NAME_BASED_ON_HEDWIG.push,
  },
  {
    value: SMART_CAMPAIGN_CHANNELS.pci,
    label: CHANNEL_DISPLAY_NAMES.pci,
    mystiqueContentType: UCD_MYSTIQUE_CHANNEL_NAMES.push,
    hedwigContentType: SMART_CAMPAIGN_CHANNEL_NAME_BASED_ON_HEDWIG.pci, // PCI is called pushinbox in backend call
  },
  // { value: 'inbox', label: CHANNEL_DISPLAY_NAMES['inbox'] },
  // { value: 'sms', label: CHANNEL_DISPLAY_NAMES['sms'] },
];

export const PUSH_CHANNEL_ACTIONS = {
  deepLink: 'deeplink',
  grabApp: 'grabApp',
  webUrl: 'webUrl',
};

export const PUSH_CHANNEL_ACTION_OPTIONS = [
  {
    label: 'Deeplink into the Grab app',
    value: PUSH_CHANNEL_ACTIONS.deepLink,
    key: PUSH_CHANNEL_ACTIONS.deepLink,
  },
  {
    label: 'Open the Grab app',
    value: PUSH_CHANNEL_ACTIONS.grabApp,
    key: PUSH_CHANNEL_ACTIONS.grabApp,
  },
  {
    label: 'Open a web page',
    value: PUSH_CHANNEL_ACTIONS.webUrl,
    key: PUSH_CHANNEL_ACTIONS.webUrl,
  },
];

export const TONE_OPTIONS_KEYS = {
  grabVoice: 'Grab Voice',
  formal: 'Formal',
  informal: 'Informal',
};

export const TONE_OPTIONS = [
  {
    value: 'Grab Voice',
    label: 'Grab Voice',
    key: TONE_OPTIONS_KEYS.grabVoice,
  },
  { value: 'Formal', label: 'Formal', key: TONE_OPTIONS_KEYS.formal },
  { value: 'Informal', label: 'Informal', key: TONE_OPTIONS_KEYS.informal },
];

export const valuePropositionOptions = [
  { value: 'Promotions/Exclusive Deals', label: 'Promotions/Exclusive Deals' },
  { value: 'Speed', label: 'Speed' },
  { value: 'Convenience', label: 'Convenience' },
  { value: 'Safety', label: 'Safety' },
  { value: 'Affordability', label: 'Affordability' },
  { value: 'Wide Selection', label: 'Wide Selection' },
  { value: 'Quality', label: 'Quality' },
  { value: 'Efficiency', label: 'Efficiency' },
];

export const TRIDENT_QUESTIONS_SLACK_CHANNEL_URL =
  'https://grab.slack.com/archives/CDLNWK4EQ';

export const SMART_CAMPAIGN_MYSTIQUE_CONTENT_NUM = 8;
export const SMART_CAMPAIGN_MYSTIQUE_GRAB_GXS = 'grab';
export const SMART_CAMPAIGN_MYSTIQUE_CRM = 'All';
export const SMART_CAMPAIGN_MYSTIQUE_CRM_l1_PERSONA = 'Generic';
export const SMART_CAMPAIGN_MYSTIQUE_CONTENT_FOR_PRIMARY_LANGUAGE = 4; // Will be used while slang mode is ON
export const SMART_CAMPAIGN_MYSTIQUE_CONTENT_FOR_SLANG = 4; // Will be used while slang mode is ON

export const WEB_URL_REGEX =
  /^((https?|ftp|smtp):\/\/)?(www.)?[a-z0-9]+(\.[a-z]{2,}){1,3}(#?\/?[a-zA-Z0-9#]+)*\/?(\?[a-zA-Z0-9-_]+=[a-zA-Z0-9-%]+&?)?$/;

export const MIN_SELECTED_VARIANTS_COUNT_IN_SMART_CAMPAIGN = 4;

export const HUMAN_READ_DATE_FORMAT = 'MM/DD/YYYY';

export const HUMAN_READ_DATE_TIME_FORMAT = 'MM/DD/YYYY HH:mm A';

export const VERTICALS_TO_IGNORED = [];

export const PROJECT_FORM_KEYS = {
  AUDIENCE: 'audience',
  COUNTRY: 'country',
  NAME: 'name',
  OBJECTIVE: 'objective',
  SHORT_NAME: 'shortName',
};


export const HEDWIG_TEMPLATE_VARIABLES = {
  DEEPLINKURL: 'deeplinkURL',
  WEBURL: 'webURL',
};

export const SMART_CAMPAIGN_INBOX_CATEGORIES = [
  {
    value: 'offers',
    label: 'Offers from Grab',
    previewDisplay: 'Offer',
    color: colorStatusNoticeAltBold,
    backgroundColor: colorStatusNoticeAltSoftest,
    icon: <OffersOutlined style={{ color: colorStatusNoticeAltBold }} />,
  },
  {
    value: 'partner-offers',
    label: 'Partner Offers',
    previewDisplay: 'Offer',
    color: colorStatusNoticeAltBold,
    backgroundColor: colorStatusNoticeAltSoftest,
    icon: <OffersOutlined style={{ color: colorStatusNoticeAltBold }} />,
  },
  {
    value: 'research',
    label: 'Feedback',
    previewDisplay: 'Feedback',
    color: colorBrandSecondaryBoldest,
    backgroundColor: colorBrandSecondarySoftest,
    icon: <FeedbackOutlined style={{ color: colorBrandSecondaryBoldest }} />,
  },
  {
    value: 'education',
    label: 'Tips and Tricks',
    previewDisplay: 'Tips',
    color: colorStatusInformativeBold,
    backgroundColor: colorStatusInformativeSoftest,
    icon: <KnowledgeUniversal style={{ color: colorStatusInformativeBold }} />,
  },
  {
    value: 'updates',
    label: 'Updates',
    previewDisplay: 'Updates',
    color: colorStatusNoticeBoldest,
    backgroundColor: colorStatusNoticeSoftest,
    icon: <NotificationsOutlined style={{ color: colorStatusNoticeBoldest }} />,
  },
];

export const OTHERS_OF_CVP = 'Others';

export const CONTENT_METADATA_OPTION_KEYS = {
  image: 'image',
  keyPointer: 'keyPointer',
};

export const PN_IMAGE_KEY = 'push';
export const SMART_CAMPAIGN_MAX_IMAGE_SIZE = 250; // max image size in kb
export const FALL_BACK_IMAGE =
  'data:image/png;base64,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';

export const DEFAULT_START_DATE = dayjs().add(4, 'day').startOf('day');
export const DEFAULT_END_DATE = DEFAULT_START_DATE.add(14, 'day').endOf('day');
export const DEFAULT_CONTROL_TIME = DEFAULT_START_DATE.hour(11).minute(0);
export const SMART_CAMPAIGN_MIN_START_DATE = dayjs().add(1, 'days');

export const MAX_NUMBER_OF_PERMUTATIONS = 4900000000;

export const SMART_CAMPAIGN_STATUS_UPDATE_TYPES = {
  REQUEST_FOR_APPROVAL: 'REQUEST_FOR_APPROVAL',
  REQUEST_FOR_WITHDRAWAL: 'REQUEST_FOR_WITHDRAWAL',
  APPROVE: 'APPROVE',
  REJECT: 'REJECT',
  DEACTIVATE: 'DEACTIVATE',
  WITHDRAW_UPCOMING: 'WITHDRAW_UPCOMING',
};

export const SMART_CAMPAIGN_CONTENT_SOURCE_MODES = {
  manual: 'Enter manually',
  automatic: 'Generate automatically',
};

// content source data for automatic and manual entry
export const SMART_CAMPAIGN_CONTENT_SOURCE_OPTIONS = [
  SMART_CAMPAIGN_CONTENT_SOURCE_MODES.automatic,
  SMART_CAMPAIGN_CONTENT_SOURCE_MODES.manual,
];

// Maximum number of content variants allowed when manually creating content for a Smart Campaign.
export const MAX_CONTENT_FOR_MANUAL_MODE = 8;

export const DYNAMIC_ATTRIBUTES_VALUES = {
  passengerName: '{{ persona.passenger_name }}',
  foodSinceTheLastOrder: '{{ persona.food_days_since_last_order_7_to_90 }}',
  foodLastOrderChainName: '{{ persona.food_last_order_chain_name }}',
  martLastOrderChainName: '{{ persona.mart_last_order_chain_name }}',
  recommendedFoodMexList: '{{ persona.pax_recommended_food_mex_list }}',
  recommendedDineoutMexList: '{{ persona.pax_recommended_dineout_mex_list }}',
  eccGrabCashbackSgdCouldHaveEarned30dOverseas:
    '{{ persona.ecc_grab_cashback_sgd_could_have_earned_30d_overseas }}',
  eccGrabCashbackSgdCouldHaveEarned30dLocal:
    '{{ persona.ecc_grab_cashback_sgd_could_have_earned_30d_local }}',
  eccGrabCashbackSgdCouldHaveEarned30d:
    '{{ persona.ecc_grab_cashback_sgd_could_have_earned_30d }}',
  eccGrabCashbackGrabcoinsCouldHaveEarned30dOverseas:
    '{{ persona.ecc_grab_cashback_grabcoins_could_have_earned_30d_overseas }}',
  eccGrabCashbackGrabcoinsCouldHaveEarned30dLocal:
    '{{ persona.ecc_grab_cashback_grabcoins_could_have_earned_30d_local }}',
  eccGrabCashbackGrabcoinsCouldHaveEarned30d:
    '{{ persona.ecc_grab_cashback_grabcoins_could_have_earned_30d }}',
};

export const DYNAMIC_ATTRIBUTES_LABEL = {
  passengerName: 'Passenger name',
  foodSinceTheLastOrder: 'Food days since last order (7 to 90)',
  foodLastOrderChainName: 'Food last ordered chain name',
  martLastOrderChainName: 'Mart last ordered chain name',
  recommendedFoodMexList: 'Food Mex Recommendation (SNP)',
  recommendedDineoutMexList: 'Dine-out Mex Recommendation (SNP)',
  eccGrabCashbackSgdCouldHaveEarned30dOverseas:
    'ECC Cashback (SGD) could have earned in last 30d (overseas)',
  eccGrabCashbackSgdCouldHaveEarned30dLocal:
    'ECC Cashback (SGD) could have earned in last 30d (local)',
  eccGrabCashbackSgdCouldHaveEarned30d:
    'ECC Cashback (SGD) could have earned in last 30d (total)',
  eccGrabCashbackGrabcoinsCouldHaveEarned30dOverseas:
    'ECC Cashback (GrabCoins) could have earned in last 30d (overseas)',
  eccGrabCashbackGrabcoinsCouldHaveEarned30dLocal:
    'ECC Cashback (GrabCoins) could have earned in last 30d (local)',
  eccGrabCashbackGrabcoinsCouldHaveEarned30d:
    'ECC Cashback (GrabCoins) could have earned in last 30d (total)',
};

export const DYNAMIC_ATTRIBUTES_LABEL_TO_VALUES_MAP = {
  [DYNAMIC_ATTRIBUTES_LABEL.passengerName]:
    DYNAMIC_ATTRIBUTES_VALUES.passengerName,
  [DYNAMIC_ATTRIBUTES_LABEL.foodSinceTheLastOrder]:
    DYNAMIC_ATTRIBUTES_VALUES.foodSinceTheLastOrder,
  [DYNAMIC_ATTRIBUTES_LABEL.foodLastOrderChainName]:
    DYNAMIC_ATTRIBUTES_VALUES.foodLastOrderChainName,
  [DYNAMIC_ATTRIBUTES_LABEL.martLastOrderChainName]:
    DYNAMIC_ATTRIBUTES_VALUES.martLastOrderChainName,
  [DYNAMIC_ATTRIBUTES_LABEL.recommendedFoodMexList]:
    DYNAMIC_ATTRIBUTES_VALUES.recommendedFoodMexList,
  [DYNAMIC_ATTRIBUTES_LABEL.recommendedDineoutMexList]:
    DYNAMIC_ATTRIBUTES_VALUES.recommendedDineoutMexList,
  [DYNAMIC_ATTRIBUTES_LABEL.eccGrabCashbackSgdCouldHaveEarned30dOverseas]:
    DYNAMIC_ATTRIBUTES_VALUES.eccGrabCashbackSgdCouldHaveEarned30dOverseas,
  [DYNAMIC_ATTRIBUTES_LABEL.eccGrabCashbackSgdCouldHaveEarned30dLocal]:
    DYNAMIC_ATTRIBUTES_VALUES.eccGrabCashbackSgdCouldHaveEarned30dLocal,
  [DYNAMIC_ATTRIBUTES_LABEL.eccGrabCashbackSgdCouldHaveEarned30d]:
    DYNAMIC_ATTRIBUTES_VALUES.eccGrabCashbackSgdCouldHaveEarned30d,
  [DYNAMIC_ATTRIBUTES_LABEL.eccGrabCashbackGrabcoinsCouldHaveEarned30dOverseas]:
    DYNAMIC_ATTRIBUTES_VALUES.eccGrabCashbackGrabcoinsCouldHaveEarned30dOverseas,
  [DYNAMIC_ATTRIBUTES_LABEL.eccGrabCashbackGrabcoinsCouldHaveEarned30dLocal]:
    DYNAMIC_ATTRIBUTES_VALUES.eccGrabCashbackGrabcoinsCouldHaveEarned30dLocal,
  [DYNAMIC_ATTRIBUTES_LABEL.eccGrabCashbackGrabcoinsCouldHaveEarned30d]:
    DYNAMIC_ATTRIBUTES_VALUES.eccGrabCashbackGrabcoinsCouldHaveEarned30d,
};

export const DYNAMIC_ATTRIBUTES_VALUE_TO_LABEL_MAP = {
  [DYNAMIC_ATTRIBUTES_VALUES.passengerName]:
    DYNAMIC_ATTRIBUTES_LABEL.passengerName,
  [DYNAMIC_ATTRIBUTES_VALUES.foodSinceTheLastOrder]:
    DYNAMIC_ATTRIBUTES_LABEL.foodSinceTheLastOrder,
  [DYNAMIC_ATTRIBUTES_VALUES.foodLastOrderChainName]:
    DYNAMIC_ATTRIBUTES_LABEL.foodLastOrderChainName,
  [DYNAMIC_ATTRIBUTES_VALUES.martLastOrderChainName]:
    DYNAMIC_ATTRIBUTES_LABEL.martLastOrderChainName,
  [DYNAMIC_ATTRIBUTES_VALUES.recommendedFoodMexList]:
    DYNAMIC_ATTRIBUTES_LABEL.recommendedFoodMexList,
  [DYNAMIC_ATTRIBUTES_VALUES.recommendedDineoutMexList]:
    DYNAMIC_ATTRIBUTES_LABEL.recommendedDineoutMexList,
  [DYNAMIC_ATTRIBUTES_VALUES.eccGrabCashbackSgdCouldHaveEarned30dOverseas]:
    DYNAMIC_ATTRIBUTES_LABEL.eccGrabCashbackSgdCouldHaveEarned30dOverseas,
  [DYNAMIC_ATTRIBUTES_VALUES.eccGrabCashbackSgdCouldHaveEarned30dLocal]:
    DYNAMIC_ATTRIBUTES_LABEL.eccGrabCashbackSgdCouldHaveEarned30dLocal,
  [DYNAMIC_ATTRIBUTES_VALUES.eccGrabCashbackSgdCouldHaveEarned30d]:
    DYNAMIC_ATTRIBUTES_LABEL.eccGrabCashbackSgdCouldHaveEarned30d,
  [DYNAMIC_ATTRIBUTES_VALUES.eccGrabCashbackGrabcoinsCouldHaveEarned30dOverseas]:
    DYNAMIC_ATTRIBUTES_LABEL.eccGrabCashbackGrabcoinsCouldHaveEarned30dOverseas,
  [DYNAMIC_ATTRIBUTES_VALUES.eccGrabCashbackGrabcoinsCouldHaveEarned30dLocal]:
    DYNAMIC_ATTRIBUTES_LABEL.eccGrabCashbackGrabcoinsCouldHaveEarned30dLocal,
  [DYNAMIC_ATTRIBUTES_VALUES.eccGrabCashbackGrabcoinsCouldHaveEarned30d]:
    DYNAMIC_ATTRIBUTES_LABEL.eccGrabCashbackGrabcoinsCouldHaveEarned30d,
};

export const MAX_SELECTED_DYNAMIC_ATTRIBUTES_COUNT = 3; // Maximum options can be selected from dynamic attributes dropdown

export const PERSONAL_ATTRIBUTE_VALUE_MATCHER = /\{\{(.*?)\}\}/g;
export const SINGLE_BRACKET_CONTENT_EXTRACTOR = /\{([^{}}]+)\}/g; // match content enclosed within single curly braces { ... }, while excluding any nested braces.
export const DOUBLE_BRACKET_CONTENT_EXTRACTOR = /\{\{([^}]+)\}\}/g;

export const VALID_PERSONAL_ATTRIBUTE_STYLE = {
  backgroundColor: '#b3ebff',
  color: '#136fd8',
};
export const INVALID_PERSONAL_ATTRIBUTE_STYLE = {
  backgroundColor: '#ffd2cc',
  color: '#d42e1c',
};

export const UNKNOWN_ATTRIBUTE = 'Unknown Attribute'; // ToolTip content for unknown attributes
// Fields to be validated for  mystique content generation
export const FIELDS_TO_VALIDATE_FOR_GENERATE_CONTENTS = [
  SMART_CAMPAIGN_FORM_KEYS.PROJECT,
  SMART_CAMPAIGN_FORM_KEYS.VERTICAL,
  SMART_CAMPAIGN_FORM_KEYS.CHANNEL,
  SMART_CAMPAIGN_FORM_KEYS.VALUE_PROPOSITION,
  SMART_CAMPAIGN_FORM_KEYS.JTBD,
];

export const MYSTIQUE_META_DATA_FIELD_NAMES = [
  // Mystique metaData fields to be validated for content generation.
  SMART_CAMPAIGN_FORM_KEYS.CALL_TO_ACTIONS,
  SMART_CAMPAIGN_FORM_KEYS.REASON_TO_USE,
  SMART_CAMPAIGN_FORM_KEYS.PRODUCT_OVERVIEW,
  SMART_CAMPAIGN_FORM_KEYS.TONE,
];

export const SLANG_DETAILS_BASED_ON_COUNTRY_AND_LANGUAGE = {
  1: {
    my: { name: 'Manglish' },
    en: { name: 'Manglish' },
  },
  2: { en: { name: 'Taglish' } },
  4: { en: { name: 'Singlish' } },
};

const enabledTypes = {};
enabledTypes.pax = [
  'push',
  'pushinbox',
  'email',
];
export const UCD_TARGET_AUDIENCES_ENABLED_TYPES = enabledTypes;
